import classNames from "classnames";
import { Entry } from "contentful";
import Link from "next/link";
import { useRouter } from "next/router";
import React from "react";

import IconInclinedStar from "@/icons/icon-inclined-star";
import Editorials from "~/types/editorials";
import { isActiveEntry } from "~/utils/editorial-utils";
import textUtils from "~/utils/text-utils";

import Icon from "../common/icon";
import MediaAsset from "../common/media-asset";
import { Breadcrumb } from "./product-breadcrumbs";
import styles from "./product-navigation-items-current-path.module.scss";

type MediaLinksProps = {
  item: Entry<Editorials.Link>;
  media: Entry<Editorials.Asset>;
  isLastElement: boolean;
};

export function MediaLinks({ item, media, isLastElement }: MediaLinksProps) {
  const router = useRouter();
  return (
    <div className={classNames(styles.wrapperLink, styles.media)}>
      <h2>
        <Link href={textUtils.sanitizeContentfulUrl(item, router)} className={styles.link}>
          <div className={styles.wrapperMedia}>
            <MediaAsset entry={media} />
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" fill="none">
              <circle
                className={styles.circle}
                opacity="0.5"
                cx="50%"
                cy="50%"
                r="24"
                stroke="url(#paint0"
                strokeWidth="2"
              />
              <defs>
                <linearGradient
                  id="paint0"
                  x1="1.58249"
                  y1="0.999815"
                  x2="45.3495"
                  y2="1.04954"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0.157279" stopColor="#DB5C00" />
                  <stop offset="1" stopColor="#E787B5" />
                </linearGradient>
              </defs>
            </svg>
          </div>
          <span className={styles.text}>{item.fields.text}</span>
        </Link>
      </h2>
      {isLastElement ? null : <Icon icon={IconInclinedStar} height={6} />}
    </div>
  );
}

type Props = {
  categoryLinks: Breadcrumb[];
  listLinks?: Entry<Editorials.Link>[];
  changeHeaderLayout?: boolean;
};

export default function ProductNavigationItemsCurrentPath({ categoryLinks, listLinks, changeHeaderLayout }: Props) {
  const router = useRouter();
  return (
    <div className={classNames(styles.containerLinks, changeHeaderLayout ? styles.changeHeaderLayout : undefined)}>
      <>
        {categoryLinks?.map((link, i) => {
          if (!link.url) {
            return null;
          }

          return (
            <div key={link.text + "-" + i} className={styles.wrapperLink}>
              <h2>
                <Link href={link.url}>{link.text}</Link>
              </h2>
            </div>
          );
        })}
      </>
      <>
        {listLinks?.map((item, i) => {
          if (!isActiveEntry(item)) return null;

          if (item.fields.image) {
            return (
              <MediaLinks
                key={`${item.sys.id}`}
                item={item}
                media={item.fields.image}
                isLastElement={i === listLinks.length - 1}
              />
            );
          }

          return (
            <div key={`${item.sys.id}`} className={styles.wrapperLink}>
              <h2>
                <Link href={textUtils.sanitizeContentfulUrl(item, router)}>{item.fields.text}</Link>
              </h2>
            </div>
          );
        })}
      </>
    </div>
  );
}
